/**
 * resources-context.tsx
 *
 * Manages saved teaching resources with:
 *  - Optimistic UI (immediate local state)
 *  - Background save via saveWithRetry (up to 3 retries, exponential backoff)
 *  - Status tracking per resource: pending → saving → saved / failed / retrying
 *  - Auth-expiry recovery: failed 401s are stored in localStorage and
 *    replayed automatically after the teacher signs back in
 *  - Browser beforeunload warning when saves are in flight or failed
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@clerk/react';
import { saveWithRetry, type SaveStatus } from './save-queue';

export { type SaveStatus };

// ── Types ─────────────────────────────────────────────────────────────────────

export type ResourceType = 'lesson' | 'activity' | 'assessment' | 'presentation';

export interface SavedResource {
  id: string;
  type: ResourceType;
  title: string;
  grade: string;
  subject: string;
  topic: string;
  standard: string;
  savedAt: string;       // ISO (created_at)
  lastEditedAt: string;  // ISO (updated_at)
  plainText: string;
  lessonData?: string;   // JSON-serialized resource data (backward compat)
  isFavorite?: boolean;
  isArchived?: boolean;
  lastViewedAt?: string | null;
  /** ID of the parent lesson resource. Set on activity/presentation/assessment companions. */
  lessonId?: string;
  /**
   * The rendering registry version (semver) that was active when this resource was last saved.
   * When this differs from the current REGISTRY_VERSION, a non-blocking "Rendering updates
   * available" badge is shown. Teachers choose when to regenerate.
   * Null/absent on resources saved before registry versioning was introduced (pre-3.1).
   */
  renderingSpecVersion?: string | null;
}

interface PendingOp {
  id: string;
  method: 'POST' | 'PATCH';
  url: string;
  body: Record<string, unknown>;
}

interface ResourcesContextType {
  resources: SavedResource[];
  loading: boolean;
  /** Per-resource save status.  Absent when idle or already cleared. */
  saveStatuses: Record<string, SaveStatus>;
  /** True when any save previously failed due to a 401 auth expiry. */
  authExpiredWork: boolean;
  /** Optimistic: returns the ID immediately; syncs to server in background. */
  saveResource: (r: Omit<SavedResource, 'id' | 'savedAt' | 'lastEditedAt'>) => string;
  /** Optimistic: applies update locally and queues a PATCH. */
  updateResource: (id: string, updates: Partial<Omit<SavedResource, 'id' | 'savedAt'>>) => void;
  /** Manually retry a failed save. */
  retrySave: (id: string) => void;
  clearAuthExpiredWork: () => void;
  deleteResource: (id: string) => Promise<void>;
  duplicateResource: (id: string) => Promise<string>;
  toggleFavorite: (id: string) => Promise<void>;
  refreshResources: () => Promise<void>;
}

const ResourcesContext = createContext<ResourcesContextType>({
  resources: [], loading: false, saveStatuses: {}, authExpiredWork: false,
  saveResource: () => '',
  updateResource: () => {},
  retrySave: () => {},
  clearAuthExpiredWork: () => {},
  deleteResource: async () => {},
  duplicateResource: async () => '',
  toggleFavorite: async () => {},
  refreshResources: async () => {},
});

export function useResources() { return useContext(ResourcesContext); }

// ── API helpers ───────────────────────────────────────────────────────────────

const API = '/api/resources';

function toSavedResource(r: Record<string, unknown>): SavedResource {
  return {
    id:           String(r.id),
    type:         r.type as ResourceType,
    title:        String(r.title ?? ''),
    grade:        String(r.grade ?? ''),
    subject:      String(r.subject ?? ''),
    topic:        String(r.topic ?? ''),
    standard:     String(r.standard ?? ''),
    savedAt:      String(r.savedAt ?? r.created_at ?? new Date().toISOString()),
    lastEditedAt: String(r.lastEditedAt ?? r.updated_at ?? new Date().toISOString()),
    plainText:    String(r.plainText ?? r.plain_text ?? ''),
    isFavorite:   Boolean(r.isFavorite),
    isArchived:   Boolean(r.isArchived),
    lastViewedAt: (r.lastViewedAt as string) ?? null,
    lessonData:             r.data ? JSON.stringify(r.data) : (r.lessonData as string | undefined),
    lessonId:               r.lesson_id ? String(r.lesson_id) : (r.lessonId as string | undefined),
    renderingSpecVersion:   (r.renderingSpecVersion as string | null | undefined) ?? null,
  };
}

// ── Auth-recovery localStorage helpers ───────────────────────────────────────

const RECOVERY_KEY = 'tb_auth_recovery';

function getRecovery(): PendingOp[] {
  try {
    return JSON.parse(localStorage.getItem(RECOVERY_KEY) ?? '[]') as PendingOp[];
  } catch {
    return [];
  }
}

function addToRecovery(op: PendingOp): void {
  const existing = getRecovery().filter(o => o.id !== op.id);
  localStorage.setItem(RECOVERY_KEY, JSON.stringify([...existing, op]));
}

function removeFromRecovery(id: string): void {
  const remaining = getRecovery().filter(o => o.id !== id);
  if (remaining.length === 0) {
    localStorage.removeItem(RECOVERY_KEY);
  } else {
    localStorage.setItem(RECOVERY_KEY, JSON.stringify(remaining));
  }
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function ResourcesProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();

  const [resources, setResources]         = useState<SavedResource[]>([]);
  const [loading, setLoading]             = useState(false);
  const [saveStatuses, setSaveStatuses]   = useState<Record<string, SaveStatus>>({});
  const [authExpiredWork, setAuthExpiredWork] = useState<boolean>(false);

  // Per-resource: stored op for manual retry
  const pendingOps  = useRef(new Map<string, PendingOp>());
  // Version counter: prevents a slow/stale retry from overwriting a newer status
  const opVersions  = useRef(new Map<string, number>());
  const hasFetched  = useRef(false);

  // ── Status helpers ──────────────────────────────────────────────────────────

  const markStatus = useCallback((id: string, ver: number, status: SaveStatus) => {
    if ((opVersions.current.get(id) ?? 0) > ver) return; // stale — discard
    setSaveStatuses(prev => ({ ...prev, [id]: status }));
    if (status === 'saved') {
      pendingOps.current.delete(id);
      // Auto-clear the "Saved ✓" badge after 4 s
      setTimeout(() => {
        setSaveStatuses(prev => {
          if (prev[id] !== 'saved') return prev;
          const { [id]: _removed, ...rest } = prev;
          return rest;
        });
      }, 4000);
    }
  }, []);

  // ── Core save executor ──────────────────────────────────────────────────────

  const executeSave = useCallback((op: PendingOp) => {
    const ver = (opVersions.current.get(op.id) ?? 0) + 1;
    opVersions.current.set(op.id, ver);
    pendingOps.current.set(op.id, op);
    setSaveStatuses(prev => ({ ...prev, [op.id]: 'pending' }));

    const fetchFn = () => fetch(op.url, {
      method: op.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(op.body),
    });

    saveWithRetry(fetchFn, (status) => markStatus(op.id, ver, status))
      .then(({ finalStatus, authExpired }) => {
        if (finalStatus === 'saved') removeFromRecovery(op.id);
        if (authExpired) {
          addToRecovery(op);
          setAuthExpiredWork(true);
        }
      });
  }, [markStatus]);

  // ── Fetch resources ─────────────────────────────────────────────────────────

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}?sort=updated_at`);
      if (!res.ok) return;
      const data = await res.json() as Record<string, unknown>[];
      setResources(data.map(toSavedResource));
    } catch {
      // Keep existing resources on transient network error
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign-in: fetch resources + replay any auth-expired work
  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      setResources([]);
      hasFetched.current = false;
      setSaveStatuses({});
      return;
    }
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchResources();
    }
    // Replay work that previously failed due to an expired session
    const recovery = getRecovery();
    if (recovery.length > 0) {
      setAuthExpiredWork(true);
      recovery.forEach(op => executeSave(op));
    }
  }, [user?.id, isLoaded, fetchResources, executeSave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn the teacher before closing/refreshing with in-flight or failed saves
  useEffect(() => {
    const hasUnsettled = Object.values(saveStatuses).some(
      s => s === 'failed' || s === 'saving' || s === 'pending' || s === 'retrying',
    );
    if (!hasUnsettled) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveStatuses]);

  // ── saveResource (optimistic, returns ID synchronously) ───────────────────

  const saveResource = useCallback((
    r: Omit<SavedResource, 'id' | 'savedAt' | 'lastEditedAt'>
  ): string => {
    const id  = `res_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const now = new Date().toISOString();

    setResources(prev => [{ ...r, id, savedAt: now, lastEditedAt: now }, ...prev]);

    const dataObj = r.lessonData
      ? (() => { try { return JSON.parse(r.lessonData!); } catch { return {}; } })()
      : {};

    executeSave({
      id, method: 'POST', url: API,
      body: {
        id,
        type:      r.type,
        title:     r.title,
        grade:     r.grade,
        subject:   r.subject,
        topic:     r.topic,
        standard:  r.standard,
        plainText: r.plainText,
        data:      dataObj,
        ...(r.lessonId             ? { lessonId:             r.lessonId             } : {}),
        ...(r.renderingSpecVersion ? { renderingSpecVersion: r.renderingSpecVersion } : {}),
      },
    });

    return id;
  }, [executeSave]);

  // ── updateResource (optimistic, queues PATCH) ─────────────────────────────

  const updateResource = useCallback((
    id: string,
    updates: Partial<Omit<SavedResource, 'id' | 'savedAt'>>
  ): void => {
    setResources(prev =>
      prev.map(r => r.id === id
        ? { ...r, ...updates, lastEditedAt: new Date().toISOString() }
        : r)
    );

    const body: Record<string, unknown> = {};
    if (updates.title      !== undefined) body.title      = updates.title;
    if (updates.plainText  !== undefined) body.plainText  = updates.plainText;
    if (updates.isFavorite !== undefined) body.isFavorite = updates.isFavorite;
    if (updates.isArchived !== undefined) body.isArchived = updates.isArchived;
    if (updates.grade      !== undefined) body.grade      = updates.grade;
    if (updates.subject    !== undefined) body.subject    = updates.subject;
    if (updates.lessonData !== undefined) {
      try { body.data = JSON.parse(updates.lessonData!); } catch { /* ignore */ }
    }
    if (updates.lessonId !== undefined) body.lessonId = updates.lessonId;

    executeSave({ id, method: 'PATCH', url: `${API}/${id}`, body });
  }, [executeSave]);

  // ── retrySave ─────────────────────────────────────────────────────────────

  const retrySave = useCallback((id: string) => {
    const op = pendingOps.current.get(id);
    if (op) executeSave(op);
  }, [executeSave]);

  const clearAuthExpiredWork = useCallback(() => setAuthExpiredWork(false), []);

  // ── deleteResource ────────────────────────────────────────────────────────

  const deleteResource = useCallback(async (id: string): Promise<void> => {
    setResources(prev => prev.filter(r => r.id !== id));
    pendingOps.current.delete(id);
    setSaveStatuses(prev => { const { [id]: _, ...rest } = prev; return rest; });
    try {
      await fetch(`${API}/${id}`, { method: 'DELETE' });
    } catch { /* optimistic delete already applied */ }
  }, []);

  // ── duplicateResource ─────────────────────────────────────────────────────

  const duplicateResource = useCallback(async (id: string): Promise<string> => {
    try {
      const res  = await fetch(`${API}/${id}/duplicate`, { method: 'POST' });
      const copy = await res.json() as Record<string, unknown>;
      const resource = toSavedResource(copy);
      setResources(prev => [resource, ...prev]);
      return resource.id;
    } catch {
      const src = resources.find(r => r.id === id);
      if (!src) return '';
      const now    = new Date().toISOString();
      const tempId = `local_${Date.now()}`;
      const copy: SavedResource = {
        ...src, id: tempId,
        title: `${src.title} (Copy)`,
        savedAt: now, lastEditedAt: now,
      };
      setResources(prev => [copy, ...prev]);
      return tempId;
    }
  }, [resources]);

  // ── toggleFavorite ────────────────────────────────────────────────────────

  const toggleFavorite = useCallback(async (id: string): Promise<void> => {
    const current = resources.find(r => r.id === id);
    if (!current) return;
    updateResource(id, { isFavorite: !current.isFavorite });
  }, [resources, updateResource]);

  return (
    <ResourcesContext.Provider value={{
      resources, loading, saveStatuses, authExpiredWork,
      saveResource, updateResource, retrySave, clearAuthExpiredWork,
      deleteResource, duplicateResource, toggleFavorite,
      refreshResources: fetchResources,
    }}>
      {children}
    </ResourcesContext.Provider>
  );
}
